// packages/shared/src/constants/designationRules.ts
// Regulation-derived rules for provider designations: note type restrictions,
// supervision defaults, and aide/tech enforcement.

import type { ClinicalNoteType } from '../types/models/noteTemplate';

// Note types fully BLOCKED per designation — cannot create at all
// Aides/techs: cannot create ANY clinical notes (unlicensed personnel)
export const DESIGNATION_FULLY_BLOCKED_NOTE_TYPES: Record<string, ClinicalNoteType[]> = {
  PT_AIDE: ['soap', 'initial_evaluation', 'progress_note', 'daily_note', 'discharge_summary', 'consultation', 'procedure_note', 'post_operative', 'reassessment'],
  OT_AIDE: ['soap', 'initial_evaluation', 'progress_note', 'daily_note', 'discharge_summary', 'consultation', 'procedure_note', 'post_operative', 'reassessment'],
  TECH: ['soap', 'initial_evaluation', 'progress_note', 'daily_note', 'discharge_summary', 'consultation', 'procedure_note', 'post_operative', 'reassessment'],
};

// Note types assistants/students can create as DRAFTS but cannot sign
// Supervising provider (PT/OT/SLP) must review and sign these
export const DESIGNATION_DRAFT_ONLY_NOTE_TYPES: Record<string, ClinicalNoteType[]> = {
  PTA: ['initial_evaluation', 'reassessment', 'progress_note', 'discharge_summary'],
  OTA: ['initial_evaluation', 'reassessment', 'progress_note', 'discharge_summary'],
  SLPA: ['initial_evaluation', 'reassessment', 'progress_note', 'discharge_summary'],
  SPT: ['initial_evaluation', 'reassessment', 'progress_note', 'discharge_summary'],
  SOT: ['initial_evaluation', 'reassessment', 'progress_note', 'discharge_summary'],
  SSLP: ['initial_evaluation', 'reassessment', 'progress_note', 'discharge_summary'],
};

// Note types that assistants cannot sign (union of draft-only — used in signing checks)
export const ASSISTANT_RESTRICTED_NOTE_TYPES: ClinicalNoteType[] = [
  'initial_evaluation', 'reassessment', 'progress_note', 'discharge_summary',
];

// Backward compat: combined map (fully blocked + draft-only) — used where full block list is needed
export const DESIGNATION_BLOCKED_NOTE_TYPES: Record<string, ClinicalNoteType[]> = {
  ...DESIGNATION_FULLY_BLOCKED_NOTE_TYPES,
  ...DESIGNATION_DRAFT_ONLY_NOTE_TYPES,
};

// Designations blocked from creating ANY clinical notes
export const DESIGNATIONS_BLOCKED_FROM_ALL_NOTES: string[] = ['PT_AIDE', 'OT_AIDE', 'TECH'];

// Assistant/student designations (can create drafts of restricted types, cannot sign them)
export const ASSISTANT_DESIGNATIONS: string[] = ['PTA', 'OTA', 'SLPA', 'SPT', 'SOT', 'SSLP'];

// Only licensed therapists can supervise aides (not PTAs/OTAs/SLPAs)
export const DESIGNATIONS_THAT_CAN_SUPERVISE_AIDES: string[] = ['PT', 'OT', 'SLP', 'MD', 'DO', 'NP', 'PA', 'DC'];

// Default supervision level by designation
export const DESIGNATION_SUPERVISION_DEFAULTS: Record<string, string> = {
  PT: 'independent',
  OT: 'independent',
  SLP: 'independent',
  MD: 'independent',
  DO: 'independent',
  NP: 'independent',
  PA: 'supervised',
  DC: 'independent',
  PTA: 'supervised',
  OTA: 'supervised',
  SLPA: 'supervised',
  SPT: 'student',
  SOT: 'student',
  SSLP: 'student',
  LMT: 'independent',
  CMT: 'independent',
  RMT: 'independent',
  PT_AIDE: 'unlicensed',
  OT_AIDE: 'unlicensed',
  TECH: 'unlicensed',
};

/**
 * Returns the FULLY blocked note types for a given designation (cannot create at all).
 * For aides/techs this is all types. For assistants this returns empty (they can create drafts).
 */
export function getBlockedNoteTypes(designation: string | undefined | null): ClinicalNoteType[] {
  if (!designation) return [];
  return DESIGNATION_FULLY_BLOCKED_NOTE_TYPES[designation] || [];
}

/**
 * Returns note types this designation can create as drafts but cannot sign.
 * Only applies to assistants (PTA/OTA/SLPA).
 */
export function getDraftOnlyNoteTypes(designation: string | undefined | null): ClinicalNoteType[] {
  if (!designation) return [];
  return DESIGNATION_DRAFT_ONLY_NOTE_TYPES[designation] || [];
}

/**
 * Returns true if the designation is allowed to create clinical notes at all.
 */
export function canCreateNotes(designation: string | undefined | null): boolean {
  if (!designation) return true;
  return !DESIGNATIONS_BLOCKED_FROM_ALL_NOTES.includes(designation);
}

/**
 * Returns true if this designation is an assistant that can create drafts
 * of restricted types but cannot sign them.
 */
export function isAssistantDesignation(designation: string | undefined | null): boolean {
  if (!designation) return false;
  return ASSISTANT_DESIGNATIONS.includes(designation);
}

// NUCC taxonomy code seeds by designation — auto-filled when a designation is
// selected and the taxonomy field is empty (or holds another seed value).
// Students/aides/techs have no standard NUCC code and are intentionally unmapped.
export const TAXONOMY_BY_DESIGNATION: Record<string, string> = {
  PT: '225100000X',
  PTA: '225200000X',
  OT: '225X00000X',
  OTA: '224Z00000X',
  SLP: '235Z00000X',
  MD: '208D00000X',
  DO: '208D00000X',
  NP: '363L00000X',
  PA: '363A00000X',
  DC: '111N00000X',
  LMT: '225700000X',
  CMT: '225700000X',
  RMT: '225700000X',
};

// Licensed independent clinicians who bill under their own NPI
export const NPI_REQUIRED_DESIGNATIONS: string[] = ['PT', 'OT', 'SLP', 'MD', 'DO', 'NP', 'PA', 'DC'];

// Everyone holding a state license: independent clinicians + licensed assistants.
// Aides/techs/students carry neither an NPI nor a license.
export const LICENSE_REQUIRED_DESIGNATIONS: string[] = [...NPI_REQUIRED_DESIGNATIONS, 'PTA', 'OTA', 'SLPA'];

export function isNpiRequired(designation: string | undefined | null): boolean {
  if (!designation) return false;
  return NPI_REQUIRED_DESIGNATIONS.includes(designation);
}

export function isLicenseRequired(designation: string | undefined | null): boolean {
  if (!designation) return false;
  return LICENSE_REQUIRED_DESIGNATIONS.includes(designation);
}

// Designations that can manage the patient problem list (longitudinal diagnoses)
export const PROBLEM_LIST_DESIGNATIONS: string[] = ['MD', 'DO', 'NP', 'PA', 'DC'];

export function canManageProblemList(designation: string | undefined | null): boolean {
  if (!designation) return false;
  return PROBLEM_LIST_DESIGNATIONS.includes(designation);
}
